// src/services/licenses.ts
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { UserLicense } from '../types';

function stripUndefined<T extends Record<string, any>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined),
  ) as T;
}

function normalizeLicense(id: string, raw: any): UserLicense {
  const jurisdictionState =
    raw?.jurisdictionState ||
    raw?.state ||
    '';

  const expiryDate =
    raw?.expiryDate ??
    raw?.endDate ??
    raw?.expiry ??
    null;

  return {
    id,
    userId: raw?.userId || '',
    licenseNumber: raw?.licenseNumber || '',
    jurisdictionCountry: raw?.jurisdictionCountry,
    jurisdictionState,
    state: jurisdictionState, // legacy alias kept hot
    type: raw?.type,
    waterType: raw?.waterType,
    tier: raw?.tier,
    purchasedAt: raw?.purchasedAt,
    startDate: raw?.startDate,
    endDate: raw?.endDate,
    status: raw?.status,
    expiryDate,
    active: typeof raw?.active === 'boolean' ? raw.active : raw?.status === 'active',
    notes: raw?.notes,
    photoUrl: raw?.photoUrl || raw?.licensePhotoUrl || undefined,
    licensePhotoUrl: raw?.licensePhotoUrl || raw?.photoUrl || undefined,
  };
}

export async function getUserLicenses(userId: string): Promise<UserLicense[]> {
  const ref = collection(db, 'users', userId, 'licenses');
  const q = query(ref, orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);

  return snap.docs.map((d) => normalizeLicense(d.id, d.data()));
}

export function subscribeToUserLicenses(
  userId: string,
  onChange: (licenses: UserLicense[]) => void,
  onError?: (err: unknown) => void,
) {
  const ref = collection(db, 'users', userId, 'licenses');
  const q = query(ref, orderBy('createdAt', 'desc'));

  return onSnapshot(
    q,
    (snap) => {
      onChange(snap.docs.map((d) => normalizeLicense(d.id, d.data())));
    },
    (err) => {
      if (onError) onError(err);
      else console.error('[licenses] subscribeToUserLicenses error', err);
    },
  );
}

export async function addUserLicense(
  userId: string,
  data: Partial<UserLicense>,
): Promise<string> {
  const id =
    (data as any).id ||
    (typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `license_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);

  const jurisdictionState =
    data.jurisdictionState ||
    data.state ||
    '';

  const payload = stripUndefined({
    ...data,
    id,
    userId,
    jurisdictionState,
    state: jurisdictionState, // legacy alias
    photoUrl: data.photoUrl || data.licensePhotoUrl || undefined,
    licensePhotoUrl: data.licensePhotoUrl || data.photoUrl || undefined,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  });

  const ref = doc(db, 'users', userId, 'licenses', id);
  await setDoc(ref, payload, { merge: true });

  return id;
}

export async function updateUserLicense(
  userId: string,
  licenseId: string,
  patch: Partial<UserLicense>,
): Promise<void> {
  const jurisdictionState =
    patch.jurisdictionState ||
    patch.state ||
    undefined;

  const payload = stripUndefined({
    ...patch,
    ...(jurisdictionState
      ? {
          jurisdictionState,
          state: jurisdictionState, // legacy alias
        }
      : {}),
    ...(patch.photoUrl || patch.licensePhotoUrl
      ? {
          photoUrl: patch.photoUrl || patch.licensePhotoUrl,
          licensePhotoUrl: patch.licensePhotoUrl || patch.photoUrl,
        }
      : {}),
    updatedAt: Date.now(),
  });

  await updateDoc(doc(db, 'users', userId, 'licenses', licenseId), payload as any);
}

export async function deleteUserLicense(
  userId: string,
  licenseId: string,
): Promise<void> {
  await deleteDoc(doc(db, 'users', userId, 'licenses', licenseId));
}